/**
 * roadmapBonusData.ts
 * Definições e prompts dos Tópicos Bônus de Voz Ativa para o Roadmap.
 */

export interface BonusTopicDef {
  id: string;             // Ex: 'b1', 'b2', 'b3'
  bonusNumber: number;    // 1, 2, 3
  afterTopicIndex: number;// 9 (após Tópico 10), 19 (após Tópico 20), 29 (após Tópico 30)
  title: Record<string, string>; // Título no idioma nativo do usuário
  getGroqInstruction: (targetLangName: string) => string;
}

export const TARGET_LANG_NAMES: Record<string, { pt: string; en: string; es: string; fr: string; de: string; it: string; hu: string; ru: string }> = {
  en: { pt: 'Inglês', en: 'English', es: 'Inglés', fr: 'Anglais', de: 'Englisch', it: 'Inglese', hu: 'Angol', ru: 'Английский' },
  fr: { pt: 'Francês', en: 'French', es: 'Francés', fr: 'Français', de: 'Französisch', it: 'Francese', hu: 'Francia', ru: 'Французский' },
  it: { pt: 'Italiano', en: 'Italian', es: 'Italiano', fr: 'Italien', de: 'Italienisch', it: 'Italiano', hu: 'Olasz', ru: 'Итальянский' },
  es: { pt: 'Espanhol', en: 'Spanish', es: 'Español', fr: 'Espagnol', de: 'Spanisch', it: 'Spagnolo', hu: 'Spanyol', ru: 'Испанский' },
  de: { pt: 'Alemão', en: 'German', es: 'Alemán', fr: 'Allemand', de: 'Deutsch', it: 'Tedesco', hu: 'Német', ru: 'Русский' },
  hu: { pt: 'Húngaro', en: 'Hungarian', es: 'Húngaro', fr: 'Hongrois', de: 'Ungarisch', it: 'Ungherese', hu: 'Magyar', ru: 'Венгерский' },
  ru: { pt: 'Russo', en: 'Russian', es: 'Ruso', fr: 'Russe', de: 'Russisch', it: 'Russo', hu: 'Orosz', ru: 'Русский' },
  pt: { pt: 'Português', en: 'Portuguese', es: 'Portugués', fr: 'Portugais', de: 'Portugiesisch', it: 'Portoghese', hu: 'Portugál', ru: 'Португальский' }
};

export const FAREWELL_INSTRUCTIONS: Record<string, string> = {
  en: "CLOSING MANDATE (5TH INTERACTION): This is your 5th and final interaction with the student in this bonus topic. Make a warm and friendly farewell COMPLETELY IN ENGLISH. Example: 'It was great practicing with you today! Keep up the good work and see you next time!' DO NOT WRITE IN PORTUGUESE OR ANY OTHER LANGUAGE.",
  fr: "CONSIGNE DE FERMETURE (5ÈME INTERACTION): C'est votre 5ème et dernière interaction avec l'étudiant dans ce sujet bonus. Faites des adieux chaleureux et amicaux ENTIÈREMENT EN FRANÇAIS. Exemple : 'C'était super de pratiquer avec vous aujourd'hui ! Continuez vos efforts et à la prochaine !' N'ÉCRIVEZ PAS EN PORTUGAIS OU DANS UNE AUTRE LANGUE.",
  es: "REGLA DE CIERRE (5ª INTERACCIÓN): Esta es tu 5ª y última interacción con el estudiante en este tema bonus. Haz una despedida cálida y amigable COMPLETAMENTE EN ESPAÑOL. Ejemplo: '¡Fue muy bueno practicar contigo hoy! ¡Sigue adelante con tus estudios y hasta la próxima!' NO ESCRIBAS EN PORTUGUÉS NI EN OTRO IDIOMA.",
  de: "SCHLUSSANWEISUNG (5. INTERAKTION): Dies ist deine 5. und letzte Interaktion mit dem Schüler in diesem Bonus-Thema. Mache einen warmen und freundlichen Abschied VOLLSTÄNDIG AUF DEUTSCH. Beispiel: 'Es war toll, heute mit dir zu üben! Mach weiter so und bis zum nächsten Mal!' SCHREIBE NICHT AUF PORTUGIESISCH ODER IN EINER ANDEREN SPRACHE.",
  it: "ISTRUZIONE DI CHIUSURA (5ª INTERAZIONE): Questa è la tua 5ª e ultima interazione con lo studente in questo argomento bonus. Fai un caloroso e amichevole saluto COMPLETAMENTE IN ITALIANO. Esempio: 'È stato fantastico esercitarsi con te oggi! Continua così e alla prossima!' NON SCRIVERE IN PORTOGHESE O IN ALTRE LINGUE.",
  hu: "ZÁRÓ UTASÍTÁS (5. INTERAKCIÓ): Ez az 5. és egyben utolsó interakciód a tanulóval ebben a bónusz témában. Tegyél egy meleg és barátságos búcsút TELJESEN MAGYARUL. Példa: 'Nagyszerű volt ma veled gyakorolni! Csak így tovább, és a legközelebbi viszontlátásra!' NE ÍRJ PORTUGÁLUL VAGY MÁS NYELVEN.",
  ru: "ИНСТРУКЦИЯ ЗАКРЫТИЯ (5-Е ВЗАИМОДЕЙСТВИЕ): Это ваше 5-е и последнее взаимодействие со студентом в этой бонусной теме. Сделайте теплое и дружеское прощание ПОЛНОСТЬЮ НА РУССКОМ ЯЗЫКЕ. Пример: 'Было здорово попрактиковаться с вами сегодня! Продолжайте в том же духе и до скорой встречи!' НЕ ПИШИТЕ НА ПОРТУГАЛЬСКОМ ИЛИ ДРУГОМ ЯЗЫКЕ.",
  pt: "REGRA DE ENCERRAMENTO (5ª INTERAÇÃO): Esta é a sua quinta e última interação com o aluno neste tópico bônus. Faça uma despedida calorosa e amigável 100% em português. Exemplo: 'Foi muito bom praticar com você hoje! Continue firme nos estudos e até a próxima!'"
};

export const PRO_REQUIRED_MESSAGES: Record<string, { title: string; message: string; btn: string; close: string }> = {
  pt: {
    title: 'Recurso PRO Exclusivo',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Seja PRO',
    close: 'Fechar'
  },
  en: {
    title: 'Exclusive PRO Feature',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Upgrade to PRO',
    close: 'Close'
  },
  es: {
    title: 'Función PRO Exclusiva',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Obtener PRO',
    close: 'Cerrar'
  },
  fr: {
    title: 'Fonctionnalité PRO Exclusive',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Devenir PRO',
    close: 'Fermer'
  },
  de: {
    title: 'Exklusive PRO-Funktion',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'PRO werden',
    close: 'Schließen'
  },
  it: {
    title: 'Funzionalità PRO Esclusiva',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Diventa PRO',
    close: 'Chiudi'
  },
  hu: {
    title: 'Exkluzív PRO Funkció',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Válts PRO-ra',
    close: 'Bezárás'
  },
  ru: {
    title: 'Эксклюзивная функция PRO',
    message: 'Esta função está disponível apenas para usuários PRO. Apoie nossa comunidade e melhore sua habilidade de conversação ainda mais rápido!',
    btn: 'Перейти на PRO',
    close: 'Закрыть'
  }
};

export const ROADMAP_BONUS_DEFS: Record<string, BonusTopicDef[]> = {
  a1: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Apresentação Pessoal',
        en: 'Bonus 1 - Personal Introduction',
        es: 'Bono 1 - Presentación Personal',
        fr: 'Bonus 1 - Présentation Personnelle',
        de: 'Bonus 1 - Persönliche Vorstellung',
        it: 'Bonus 1 - Presentazione Personale',
        hu: 'Bónusz 1 - Bemutatkozás',
        ru: 'Бонус 1 - Самопрезентация'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. O usuário concluiu o bloco inicial de A1. Inicie a conversa de forma extremamente amigável e simples no idioma de estudo (${lang}): 'Olá! Sou o professor LibriTui. Como você se chama?' Conduza um diálogo básico perguntando de onde ele é e idade, mantendo frases curtas.`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Minha Rotina e Hobbies',
        en: 'Bonus 2 - My Routine and Hobbies',
        es: 'Bono 2 - Mi Rutina y Hobbies',
        fr: 'Bonus 2 - Ma Routine et Loisirs',
        de: 'Bonus 2 - Meine Routine und Hobbys',
        it: 'Bonus 2 - La Mia Routine e Hobbies',
        hu: 'Bónusz 2 - Napi Rutin és Hobbik',
        ru: 'Бонус 2 - Моя Рутина и Хобби'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie cumprimentando o aluno e puxando assunto sobre o dia a dia dele de forma simples no idioma de estudo (${lang}): 'Olá novamente! O que você gosta de fazer no seu tempo livre?' Continue conversando sobre gostos e rotina básica.`
    },
    {
      id: 'b3',
      bonusNumber: 3,
      afterTopicIndex: 29, // após Tópico 30
      title: {
        pt: 'Bônus 3 - Família e Casa',
        en: 'Bonus 3 - Family and Home',
        es: 'Bono 3 - Familia y Hogar',
        fr: 'Bonus 3 - Famille et Maison',
        de: 'Bonus 3 - Familie und Zuhause',
        it: 'Bonus 3 - Famiglia e Casa',
        hu: 'Bónusz 3 - Család és Otthon',
        ru: 'Бонус 3 - Семья и Дом'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie o papo perguntando sobre a família do aluno de forma acolhedora no idioma de estudo (${lang}): 'Olá! Você tem irmãos ou pets? Como é a sua casa?' Pratique vocabulário básico de casa e família.`
    }
  ],
  a2: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Ontem e Fim de Semana',
        en: 'Bonus 1 - Yesterday and Weekend',
        es: 'Bono 1 - Ayer y Fin de Semana',
        fr: 'Bonus 1 - Hier et le Week-end',
        de: 'Bonus 1 - Gestern und Wochenende',
        it: 'Bonus 1 - Ieri e il Fine Settimana',
        hu: 'Bónusz 1 - Tegnap és Hétvége',
        ru: 'Бонус 1 - Вчера и Выходные'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie a conversa perguntando sobre o passado recente do aluno usando estruturas simples no idioma de estudo (${lang}): 'Olá! Como foi o seu último fim de semana? O que você fez?'`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Compras e Comida',
        en: 'Bonus 2 - Shopping and Food',
        es: 'Bono 2 - Compras y Comida',
        fr: 'Bonus 2 - Achats et Nourriture',
        de: 'Bonus 2 - Einkaufen und Essen',
        it: 'Bonus 2 - Acquisti e Cibo',
        hu: 'Bónusz 2 - Vásárlás és Étel',
        ru: 'Бонус 2 - Покупки и Еда'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie o diálogo simulando um contexto prático no idioma de estudo (${lang}): 'Olá! Qual é a sua comida favorita e onde você costuma comprar os ingredientes?'`
    },
    {
      id: 'b3',
      bonusNumber: 3,
      afterTopicIndex: 29, // após Tópico 30
      title: {
        pt: 'Bônus 3 - Planos para o Futuro',
        en: 'Bonus 3 - Future Plans',
        es: 'Bono 3 - Planes para el Futuro',
        fr: 'Bonus 3 - Projets d\'Avenir',
        de: 'Bonus 3 - Zukunftspläne',
        it: 'Bonus 3 - Progetti Futuri',
        hu: 'Bónusz 3 - Jövőbeli Tervek',
        ru: 'Бонус 3 - Планы на Будущее'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie a conversa puxando planos futuros no idioma de estudo (${lang}): 'Olá! Quais são os seus planos para as próximas férias ou para o próximo ano?'`
    }
  ],
  b1: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Experiências de Viagem',
        en: 'Bonus 1 - Travel Experiences',
        es: 'Bono 1 - Experiencias de Viaje',
        fr: 'Bonus 1 - Expériences de Voyage',
        de: 'Bonus 1 - Reiseerfahrungen',
        it: 'Bonus 1 - Esperienze di Viaggio',
        hu: 'Bónusz 1 - Utazási Élmények',
        ru: 'Бонус 1 - Опыт Путешествий'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie conversando sobre viagens de forma mais natural no idioma de estudo (${lang}): 'Olá! Conte-me sobre a viagem mais interessante que você já fez na vida. O que você viu por lá?'`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Opiniões sobre Tecnologia',
        en: 'Bonus 2 - Technology Opinions',
        es: 'Bono 2 - Opiniones sobre Tecnología',
        fr: 'Bonus 2 - Opinions sur la Technologie',
        de: 'Bonus 2 - Meinungen zur Technologie',
        it: 'Bonus 2 - Opinioni sulla Tecnologia',
        hu: 'Bónusz 2 - Vélemények a Technológiáról',
        ru: 'Бонус 2 - Мнения о Технологиях'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie o papo com um tema cotidiano mais elaborado no idioma de estudo (${lang}): 'Olá! Na sua opinião, a tecnologia moderna facilita ou complica a nossa vida diária? Por quê?'`
    }
  ],
  b2: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Desafios Profissionais e Metas',
        en: 'Bonus 1 - Career Challenges and Goals',
        es: 'Bono 1 - Desafíos Profesionales y Metas',
        fr: 'Bonus 1 - Défis Professionnels et Objectifs',
        de: 'Bonus 1 - Berufliche Herausforderungen und Ziele',
        it: 'Bonus 1 - Sfide Professionali e Obiettivi',
        hu: 'Bónusz 1 - Karrier Kihívások és Célok',
        ru: 'Бонус 1 - Карьерные Вызовы и Цели'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie a conversa em um tom mais fluido e exigente no idioma de estudo (${lang}): 'Olá! Quais são os maiores desafios na sua carreira ou estudos atualmente e como você planeja superá-los?'`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Cultura, Arte e Meio Ambiente',
        en: 'Bonus 2 - Culture, Art and Environment',
        es: 'Bono 2 - Cultura, Arte y Medio Ambiente',
        fr: 'Bonus 2 - Culture, Art et Environnement',
        de: 'Bonus 2 - Kultur, Kunst und Umwelt',
        it: 'Bonus 2 - Cultura, Arte e Ambiente',
        hu: 'Bónusz 2 - Kultúra, Művészet és Környezet',
        ru: 'Бонус 2 - Культура, Искусство и Экология'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie debatendo um tema abstrato no idioma de estudo (${lang}): 'Olá! Você acha que a arte e a cultura têm o poder de mudar a sociedade atual? Qual sua visão sobre isso?'`
    }
  ],
  c1: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Dilemas Éticos e Sociais',
        en: 'Bonus 1 - Ethical and Social Dilemmas',
        es: 'Bono 1 - Dilemas Éticos y Sociales',
        fr: 'Bonus 1 - Dilemmes Éthiques et Sociaux',
        de: 'Bonus 1 - Ethische und Soziale Dilemmata',
        it: 'Bonus 1 - Dilemmi Etici e Sociali',
        hu: 'Bónusz 1 - Etikai és Társadalmi Dilemmák',
        ru: 'Бонус 1 - Этические и Социальные Дилеммы'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie a conversa exigindo vocabulário rico e estruturas complexas no idioma de estudo (${lang}): 'Olá! Discuta comigo: até que ponto a inteligência artificial deve impactar o mercado de trabalho global?'`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Tendências Globais e Futuro',
        en: 'Bonus 2 - Global Trends and Future',
        es: 'Bono 2 - Tendencias Globales y Futuro',
        fr: 'Bonus 2 - Tendances Mondiales et Avenir',
        de: 'Bonus 2 - Globale Trends und Zukunft',
        it: 'Bonus 2 - Tendenze Globali e Futuro',
        hu: 'Bónusz 2 - Globális Trendek és A Jövő',
        ru: 'Бонус 2 - Глобальные Тренды и Будущее'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie o diálogo debatendo previsões e cenários complexos para o futuro da sociedade no idioma de estudo (${lang}).`
    }
  ],
  c2: [
    {
      id: 'b1',
      bonusNumber: 1,
      afterTopicIndex: 9, // após Tópico 10
      title: {
        pt: 'Bônus 1 - Filosofia, Linguagem e Nuances',
        en: 'Bonus 1 - Philosophy, Language and Nuances',
        es: 'Bono 1 - Filosofía, Lenguaje y Matices',
        fr: 'Bonus 1 - Philosophie, Langage et Nuances',
        de: 'Bonus 1 - Philosophie, Sprache und Nuancen',
        it: 'Bonus 1 - Filosofia, Linguaggio e Sfumature',
        hu: 'Bónusz 1 - Filozófia, Nyelv és Árnyalatok',
        ru: 'Бонус 1 - Философия, Язык и Нюансы'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie a conversa em nível nativo/sofisticado no idioma de estudo (${lang}), explorando expressões idiomáticas avançadas, metáforas e registros cultos.`
    },
    {
      id: 'b2',
      bonusNumber: 2,
      afterTopicIndex: 19, // após Tópico 20
      title: {
        pt: 'Bônus 2 - Análise Crítica e Retórica',
        en: 'Bonus 2 - Critical Analysis and Rhetoric',
        es: 'Bono 2 - Análisis Crítico y Retórica',
        fr: 'Bonus 2 - Analyse Critique et Rhétorique',
        de: 'Bonus 2 - Analyse Critique et Rhétorique',
        it: 'Bonus 2 - Analisi Critica e Retorica',
        hu: 'Bónusz 2 - Kritikai Elemzés és Rétotika',
        ru: 'Бонус 2 - Критический Анализ и Риторика'
      },
      getGroqInstruction: (lang) =>
        `Atue como o Professor LibriTui. Inicie o desafio final de conversação no idioma de estudo (${lang}) exigindo o máximo de eloquência, precisão lexical e argumentação refinada.`
    }
  ]
};
